import { useTranslation } from 'react-i18next';

/**
 * The single front-end declaration, mirroring `backend/constants/permissions.js`.
 * `utils/permissionUtils` re-exports it: there used to be a second, shorter copy there, and the
 * two had drifted by five entries — deployments, issues and groups were unreachable from the
 * grant dialog even for a super admin who held them.
 */
export const PERMISSIONS = {
  DASHBOARD_ACCESS: 'dashboard:access',
  DASHBOARD_USERS_VIEW: 'dashboard:users:view',
  DASHBOARD_USERS_MANAGE: 'dashboard:users:manage',
  DASHBOARD_SUPERADMINS_MANAGE: 'dashboard:superadmins:manage',
  DASHBOARD_TECHNICIANS_VIEW: 'dashboard:technicians:view',
  DASHBOARD_TECHNICIANS_MANAGE: 'dashboard:technicians:manage',
  DASHBOARD_QUOTAS_VIEW: 'dashboard:quotas:view',
  DASHBOARD_QUOTAS_MANAGE: 'dashboard:quotas:manage',
  DASHBOARD_STOCKS_VIEW: 'dashboard:stocks:view',
  DASHBOARD_DEPLOYMENTS_VIEW: 'dashboard:deployments:view',
  DASHBOARD_DEPLOYMENTS_MANAGE: 'dashboard:deployments:manage',
  DASHBOARD_ISSUES_VIEW: 'dashboard:issues:view',
  DASHBOARD_GROUPS_VIEW: 'dashboard:groups:view',
  DASHBOARD_GROUPS_MANAGE: 'dashboard:groups:manage',
  DASHBOARD_REFERENTIALS_VIEW: 'dashboard:referentials:view',
  DASHBOARD_REFERENTIALS_MANAGE: 'dashboard:referentials:manage',
  PUSH_NOTIFICATIONS_SEND: 'push-notifications:send',
  DASHBOARD_AUDIT_VIEW: 'dashboard:audit:view',
};

export const DEFAULT_PERMISSIONS_BY_ROLE = {
  user: [],
  manager: [],
  technician: [],
  admin: [
    PERMISSIONS.DASHBOARD_ACCESS,
    PERMISSIONS.DASHBOARD_USERS_VIEW,
    PERMISSIONS.DASHBOARD_USERS_MANAGE,
    PERMISSIONS.DASHBOARD_ISSUES_VIEW,
    PERMISSIONS.DASHBOARD_GROUPS_VIEW,
    PERMISSIONS.DASHBOARD_GROUPS_MANAGE,
  ],
  // Technicians are cross-desk, so seeing them is opt-in for a desk admin
  superadmin: [
    PERMISSIONS.DASHBOARD_ACCESS,
    PERMISSIONS.DASHBOARD_USERS_VIEW,
    PERMISSIONS.DASHBOARD_USERS_MANAGE,
    PERMISSIONS.DASHBOARD_TECHNICIANS_VIEW,
    PERMISSIONS.DASHBOARD_TECHNICIANS_MANAGE,
    PERMISSIONS.DASHBOARD_QUOTAS_VIEW,
    PERMISSIONS.DASHBOARD_QUOTAS_MANAGE,
    PERMISSIONS.DASHBOARD_STOCKS_VIEW,
    PERMISSIONS.DASHBOARD_DEPLOYMENTS_VIEW,
    PERMISSIONS.DASHBOARD_DEPLOYMENTS_MANAGE,
    PERMISSIONS.DASHBOARD_ISSUES_VIEW,
    PERMISSIONS.DASHBOARD_GROUPS_VIEW,
    PERMISSIONS.DASHBOARD_GROUPS_MANAGE,
    PERMISSIONS.DASHBOARD_REFERENTIALS_VIEW,
    PERMISSIONS.DASHBOARD_REFERENTIALS_MANAGE,
    PERMISSIONS.PUSH_NOTIFICATIONS_SEND,
    PERMISSIONS.DASHBOARD_AUDIT_VIEW,
  ],
};

/** Localized permission label; literal switch for the i18n guard. Unknown id -> the raw id (CA26). @returns {string} */
export const permissionLabel = (t, id) => {
  switch (id) {
    case PERMISSIONS.DASHBOARD_ACCESS:
      return t('Dashboard access');
    case PERMISSIONS.DASHBOARD_USERS_VIEW:
      return t('View users');
    case PERMISSIONS.DASHBOARD_USERS_MANAGE:
      return t('Manage users');
    case PERMISSIONS.DASHBOARD_SUPERADMINS_MANAGE:
      return t('Manage super admins');
    case PERMISSIONS.DASHBOARD_TECHNICIANS_VIEW:
      return t('View technicians');
    case PERMISSIONS.DASHBOARD_TECHNICIANS_MANAGE:
      return t('Manage technicians');
    case PERMISSIONS.DASHBOARD_QUOTAS_VIEW:
      return t('View quotas');
    case PERMISSIONS.DASHBOARD_QUOTAS_MANAGE:
      return t('Manage quotas');
    case PERMISSIONS.DASHBOARD_STOCKS_VIEW:
      return t('View stocks');
    case PERMISSIONS.DASHBOARD_DEPLOYMENTS_VIEW:
      return t('View deployments');
    case PERMISSIONS.DASHBOARD_DEPLOYMENTS_MANAGE:
      return t('Manage deployments');
    case PERMISSIONS.DASHBOARD_ISSUES_VIEW:
      return t('View issues');
    case PERMISSIONS.DASHBOARD_GROUPS_VIEW:
      return t('View groups');
    case PERMISSIONS.DASHBOARD_GROUPS_MANAGE:
      return t('Manage groups');
    case PERMISSIONS.DASHBOARD_REFERENTIALS_VIEW:
      return t('View referentials');
    case PERMISSIONS.DASHBOARD_REFERENTIALS_MANAGE:
      return t('Manage referentials');
    case PERMISSIONS.PUSH_NOTIFICATIONS_SEND:
      return t('Send push notifications');
    case PERMISSIONS.DASHBOARD_AUDIT_VIEW:
      return t('View the action journal');
    default:
      return id;
  }
};

/**
 * Hook returning a resolver that translates a permission id into a localized label.
 * @returns {(id: string) => string}
 */
export const usePermissionLabel = () => {
  const { t } = useTranslation();

  return (id) => permissionLabel(t, id);
};

/**
 * Get default permissions for a role
 * @param {string} role - User role
 * @returns {string[]} Array of permission strings
 */
export const getDefaultPermissions = (role) => {
  return DEFAULT_PERMISSIONS_BY_ROLE[role] || [];
};

/**
 * Every permission, as options for the grant dialogs. No label: both callers resolve the wording
 * through `usePermissionLabel`, so one carried here would be a second, untranslated copy.
 * @returns {Array<{value: string}>}
 */
export const getAllPermissions = () => Object.values(PERMISSIONS).map((permission) => ({ value: permission }));
